import { useTranslation } from "react-i18next";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import styles from "./styles.module.scss";
import AmountCard from "./AmountCard";
import classNames from "classnames";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ContractContext } from "../../../../../providers/contract";
import TextInput, {
  InputType,
} from "../../../../../components/forms/textInput";
import {
  handleCompressImage,
  roundToTwoDecimals,
} from "../../../../../setup/util/helpers";
import { uploadFile } from "../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../s3Integration/enums";
import ImageUpload from "../../../../../components/forms/imageUpload";
import { CircularProgress } from "@mui/material";
import PDFComponent from "../../../../../components/common/PDFComponent";
import Textarea from "../../../../../components/forms/textarea";
import { getUserId } from "../../../../../setup/auth/oidc";
import {
  AddOnFeeCategory,
  CashOutAmount,
  ESenderRole,
  IWaffyExtraFees,
} from "../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../setup/api/services/contractActions/enum";
import {
  doAction,
  getAdminActorRole,
  getSettlementActorRole,
} from "../../../../../setup/api/services/contractActions";
import { getShipmentFeesTotal } from "../../../../../setup/util/contract";
import WaffyFeesSummary from "../components/WaffyFeesSummary";
import {
  compareUserIdentifiers,
  getUserIdentificationFromContractParties,
} from "../../../../../setup/util/userIdentifier";
import { getUnregisteredContractParties } from "../../../../../setup/util/partyRegistration";
import UnregisteredPartiesAlert from "../../UnregisteredPartiesAlert";

interface ManualSettlementProps {
  propose?: boolean;
  onSuccess?: () => void;
}

const ManualSettlement = ({ propose, onSuccess }: ManualSettlementProps) => {
  const { t } = useTranslation();
  const { contract, actions } = useContext(ContractContext);

  const userId = getUserId();

  const [sellerValue, setSellerValue] = useState<number | undefined>(undefined);
  const [sellerFee, setSellerFee] = useState<number | undefined>(undefined);
  const [buyerValue, setBuyerValue] = useState<number | undefined>(undefined);
  const [buyerFee, setBuyerFee] = useState<number | undefined>(undefined);
  const [showSettlementError, setShowSettlementError] = useState(false);
  const [showWaffyFeesError, setShowWaffyFeesError] = useState(false);
  const [waffyFeesPercentage, setWaffyFeesPercentage] = useState<number>(
    (contract?.feePercentage ?? 0.04) * 100
  );
  const [description, setDescription] = useState<string>("");
  const [entities, setEntities] = useState<
    {
      file: File;
      s3File: string;
      isLoading: boolean;
      loadingProgress: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [naqelFees, setNaqelFees] = useState<number | undefined>(0);
  const [naqelFeesDisplay, setNaqelFeesDisplay] = useState<string>("0");
  const providerIdentification = getUserIdentificationFromContractParties(
    contract,
    ESenderRole.PROVIDER
  );

  const customerIdentification = getUserIdentificationFromContractParties(
    contract,
    ESenderRole.CUSTOMER
  );

  useEffect(() => {
    if (!contract) return;
    const list = contract.cashOutAmountList;
    const hasList = list?.length;
    const hasWaffyExtra =
      contract.waffyExtraFees?.length &&
      contract.waffyExtraFees.some(
        (f) => f.type === AddOnFeeCategory.SHIPPING_FEES
      );
    if (!hasList && !hasWaffyExtra) return;

    const naqelParty = contract.parties?.find(
      (p) => p.role === ESenderRole.SERVICE_PROVIDER
    );
    const naqelUserIdentification =
      naqelParty?.userId != null ? Number(naqelParty.userId) : undefined;

    const isNaqelEntry = (item: CashOutAmount) =>
      item.amountDue != null &&
      compareUserIdentifiers(item.userIdentification, naqelUserIdentification);

    if (list?.length) {
      for (const item of list) {
        const itemAmountDue = item.amountDue;
        const itemFeeBasedOnPercentage =
          item.feePercentage && itemAmountDue
            ? item.feePercentage * itemAmountDue
            : undefined;
        const itemFee = item.fee ?? itemFeeBasedOnPercentage;

        if (
          item.userIdentification === providerIdentification &&
          itemAmountDue != null
        ) {
          setSellerValue(roundToTwoDecimals(itemAmountDue));
          setSellerFee(roundToTwoDecimals(itemFee));
        } else if (
          item.userIdentification === customerIdentification &&
          itemAmountDue != null
        ) {
          setBuyerValue(roundToTwoDecimals(itemAmountDue));
          setBuyerFee(roundToTwoDecimals(itemFee));
        } else if (isNaqelEntry(item)) {
          setNaqelFees(roundToTwoDecimals(itemAmountDue));
          setNaqelFeesDisplay(
            itemAmountDue != null
              ? String(roundToTwoDecimals(itemAmountDue))
              : ""
          );
        }
      }
      const totalFee = list.reduce((sum, i) => sum + (i.fee ?? 0), 0);
      const totalAmount = roundToTwoDecimals(
        (contract.originalItemPrice ?? contract.itemPrice) +
          getShipmentFeesTotal(contract)
      );
      if (totalAmount && totalAmount > 0 && totalFee >= 0) {
        setWaffyFeesPercentage(
          Math.round((totalFee / totalAmount) * 100 * 100) / 100
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  const chargedShipmentFee = getShipmentFeesTotal(contract);

  const shippingFeeAmount = useMemo(() => {
    if (
      contract?.shipment?.feeBreakdown?.settled &&
      chargedShipmentFee &&
      contract?.shipment?.feeBreakdown?.settled != chargedShipmentFee
    ) {
      return roundToTwoDecimals(contract?.shipment?.feeBreakdown?.settled);
    }

    if (!contract?.addonFeesList?.length) return 0;

    return roundToTwoDecimals(chargedShipmentFee) ?? 0;
  }, [
    contract?.addonFeesList,
    contract?.shipment?.feeBreakdown?.settled,
    chargedShipmentFee,
  ]);

  const totalAmountDue = useMemo(() => {
    const total =
      (contract?.originalItemPrice ?? contract?.itemPrice ?? 0) +
      chargedShipmentFee;
    return roundToTwoDecimals(total);
  }, [contract?.itemPrice, contract?.originalItemPrice, chargedShipmentFee]);

  const maxBuyerAmount = roundToTwoDecimals(
    (contract?.itemPrice ?? 0) + getShipmentFeesTotal(contract)
  );

  const waffyExtraFeesAmount = useMemo(() => {
    const fees = naqelFees || 0;
    const remaining =
      totalAmountDue - ((buyerValue || 0) + (sellerValue || 0) + fees);
    const maxWaffyExtraFees = shippingFeeAmount - fees;
    const waffyExtraFees = Math.min(remaining, maxWaffyExtraFees);
    const positiveWaffyExtraFees = waffyExtraFees > 0 ? waffyExtraFees : 0;
    return roundToTwoDecimals(positiveWaffyExtraFees);
  }, [buyerValue, naqelFees, sellerValue, shippingFeeAmount, totalAmountDue]);

  const totalSettlementAmount = useMemo(() => {
    const total = totalAmountDue
      ? roundToTwoDecimals(
          totalAmountDue - ((naqelFees || 0) + waffyExtraFeesAmount)
        )
      : 0;
    return total;
  }, [naqelFees, totalAmountDue, waffyExtraFeesAmount]);

  const remainingSettlementAmount = useMemo(() => {
    if (!totalSettlementAmount) return 0;
    const remaining =
      totalSettlementAmount - ((sellerValue || 0) + (buyerValue || 0));
    return roundToTwoDecimals(remaining);
  }, [sellerValue, buyerValue, totalSettlementAmount]);

  // a settlement cannot be sent for a party the backend cannot resolve to a
  // real user, so every party has to be registered with a valid id first
  const unregisteredParties = useMemo(
    () => getUnregisteredContractParties(contract),
    [contract]
  );

  const buttonDisabled = useCallback(() => {
    if (loading) return true;
    if (unregisteredParties.length) return true;
    if (!sellerValue && !buyerValue) {
      return true;
    }
    if (
      (sellerValue && sellerFee === undefined) ||
      (buyerValue && buyerFee === undefined) ||
      (!sellerValue && sellerFee) ||
      (!buyerValue && buyerFee)
    ) {
      return true;
    }
    return false;
  }, [
    sellerValue,
    sellerFee,
    buyerValue,
    buyerFee,
    loading,
    unregisteredParties.length,
  ]);

  const waffyFeesAmount = roundToTwoDecimals(
    (totalAmountDue * waffyFeesPercentage) / 100
  );

  const handleSettleContract = useCallback(async () => {
    setShowSettlementError(false);
    setShowWaffyFeesError(false);

    if (!contract) return;
    if (!userId) return;
    if (unregisteredParties.length) return;

    let maxAmount = contract?.itemPrice + chargedShipmentFee;
    if (contract?.originalItemPrice) {
      if ((sellerValue && !buyerValue) || (sellerValue && buyerValue)) {
        maxAmount = roundToTwoDecimals(
          contract.originalItemPrice + chargedShipmentFee
        );
      }
    }

    if (
      Number(
        roundToTwoDecimals(
          (sellerValue || 0) +
            (buyerValue || 0) +
            (naqelFees || 0) +
            waffyExtraFeesAmount
        )
      ) !== roundToTwoDecimals(maxAmount)
    ) {
      setShowSettlementError(true);
      return;
    }

    if (
      waffyFeesPercentage < 0 ||
      waffyFeesPercentage > 100 ||
      roundToTwoDecimals((buyerFee ?? 0) + (sellerFee ?? 0)) !==
        roundToTwoDecimals(waffyFeesAmount)
    ) {
      setShowWaffyFeesError(true);
      return;
    }

    setLoading(true);

    const waffyExtraFees: IWaffyExtraFees[] | undefined =
      waffyExtraFeesAmount && contract?.shippable
        ? [
            {
              amount: waffyExtraFeesAmount,
              description: null,
              type: AddOnFeeCategory.SHIPPING_FEES,
            },
          ]
        : undefined;

    const cashOutAmountList: {
      id: string;
      amountDue: number;
      fee?: number;
      feePercentage?: number;
    }[] = [];

    if (
      providerIdentification &&
      sellerValue !== undefined &&
      sellerFee !== undefined
    ) {
      cashOutAmountList.push({
        id: providerIdentification,
        amountDue: sellerValue,
        fee: sellerFee,
      });
    }

    if (
      customerIdentification &&
      buyerValue !== undefined &&
      buyerFee !== undefined
    ) {
      cashOutAmountList.push({
        id: customerIdentification,
        amountDue: buyerValue,
        fee: buyerFee,
      });
    }

    const naqelServiceProvider = contract?.parties?.find(
      (party) => party.role === ESenderRole.SERVICE_PROVIDER
    );

    if (naqelFees && naqelServiceProvider?.userId) {
      cashOutAmountList.push({
        id: naqelServiceProvider?.userId,
        amountDue: naqelFees,
        feePercentage: 0,
      });
    }

    let cashOutAttachments: string[] | undefined = undefined;

    if (entities.length)
      cashOutAttachments = entities.map((entity) => entity?.s3File);

    try {
      if (propose) {
        const providerUid = providerIdentification;
        const customerUid = customerIdentification;

        const proposedSettlement: {
          id: string;
          amountDue: number;
          fee?: number;
        }[] = [];

        if (
          sellerValue !== undefined &&
          sellerFee !== undefined &&
          providerUid
        ) {
          proposedSettlement.push({
            id: providerUid,
            amountDue: sellerValue - (sellerFee ?? 0),
          });
        }
        if (buyerValue !== undefined && buyerFee !== undefined && customerUid) {
          proposedSettlement.push({
            id: customerUid,
            amountDue: buyerValue - (buyerFee ?? 0),
          });
        }

        const brokerUserIdentification = contract?.parties?.find(
          (p) => p.role == ESenderRole.BROKER
        )?.userId;

        if (brokerUserIdentification) {
          proposedSettlement.push({
            id: String(brokerUserIdentification),
            amountDue: (sellerFee ?? 0) + (buyerFee ?? 0),
          });
        }

        const resolution =
          (sellerValue || 0) >= (buyerValue || 0)
            ? "RESOLVED_PROVIDER"
            : "RESOLVED_CUSTOMER";

        await doAction({
          contractId: contract?.id,
          userId: String(userId),
          actorRole: getAdminActorRole(),
          contractAction: EContractActions.ADMIN_RESOLVE_ESCALATION,
          escalationDTO: {
            resolution,
            proposedSettlement,
          },
        } as any);
        setLoading(false);
        actions.getContract();
        onSuccess?.();
      } else {
        const body = {
          contractId: contract?.id,
          userId: userId,
          actorRole: getSettlementActorRole(),
          senderId: String(contract?.senderId),
          contractAction: EContractActions.ADMIN_SETTLE_CONTRACT,
          cashOutAmountList: cashOutAmountList,
          cashOutComment: description || undefined,
          cashOutAttachments,
          ...(waffyExtraFees && { waffyExtraFees }),
        };
        await doAction(body);
        setLoading(false);
        window.location.reload();
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  }, [
    actions,
    buyerFee,
    buyerValue,
    chargedShipmentFee,
    contract,
    customerIdentification,
    description,
    entities,
    naqelFees,
    onSuccess,
    propose,
    providerIdentification,
    sellerFee,
    sellerValue,
    unregisteredParties.length,
    userId,
    waffyExtraFeesAmount,
    waffyFeesAmount,
    waffyFeesPercentage,
  ]);

  const handleFileUpload = useCallback(
    async (e: any) => {
      const tempEntities: {
        file: File;
        s3File: string;
        isLoading: boolean;
        loadingProgress: number;
      }[] = [...entities];
      const allowedFormats = [
        "image/x-png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/png",
        "application/pdf",
      ];
      try {
        const totalFiles =
          e?.target?.files?.length + entities.length > 10
            ? 10 - entities.length
            : e?.target?.files?.length;

        setEntities((prevEntities) => {
          return [
            ...prevEntities,
            ...Array.from(Array(totalFiles).keys()).map(() => ({
              file: undefined as unknown as File,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            })),
          ];
        });

        for (let i = 0; i < totalFiles; i++) {
          const file = e?.target?.files[i];

          if (allowedFormats.indexOf(file?.type) === -1) {
            return;
          }

          if (file?.type !== "application/pdf") {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });

            const image = await handleCompressImage(file);

            if (image) {
              const entryIndex = tempEntities.findIndex(
                (entity) => entity?.file?.name === image?.name
              );

              tempEntities[entryIndex].file = image;
            }
          } else {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });
          }

          if (i === totalFiles - 1) setEntities(tempEntities);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [entities]
  );

  const uploadFiles = useCallback(async () => {
    const entityArray = [...entities];
    const uploadRequests: Promise<void>[] = [];
    for (let i = 0; i < entityArray?.length; i++) {
      const file = entityArray?.[i]?.file;

      if (
        entityArray?.[i]?.file &&
        entityArray?.[i]?.isLoading &&
        entityArray?.[i]?.loadingProgress === 0 &&
        !entityArray?.[i]?.s3File
      ) {
        const uploadRequest = uploadFile(
          file,
          EUploadImageType.CONTRACT,
          "TEMP_WAFFY_SYSTEM",
          (progress) =>
            setEntities((prevEntities) => {
              const newEntities = prevEntities?.map((entity) => {
                if (entity.file.name === file.name) {
                  return {
                    ...entity,
                    loadingProgress: Math.round(
                      (progress?.loaded / progress?.total) * 100
                    ),
                  };
                }
                return entity;
              });
              return newEntities;
            })
        ).then((url) => {
          setEntities((prevEntities) => {
            const newEntities = prevEntities?.map((entity) => {
              if (entity.file.name === file.name) {
                return {
                  file,
                  s3File: url,
                  isLoading: false,
                  loadingProgress: 0,
                };
              }

              return entity;
            });
            return newEntities;
          });
        });

        uploadRequests.push(uploadRequest);
      }
    }

    await Promise.all(uploadRequests);
  }, [entities]);

  useEffect(() => {
    if (
      entities.length > 0 &&
      entities.some(
        (entity) => entity?.isLoading && entity.loadingProgress === 0
      )
    )
      uploadFiles();
  }, [entities, uploadFiles]);

  const handleDeleteFile = useCallback(
    (index: number) => {
      const tempEntities = [...entities];
      tempEntities.splice(index, 1);
      setEntities(tempEntities);
    },
    [entities]
  );

  const handleNaqelFeesChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value;
      setNaqelFeesDisplay(raw);
      setNaqelFees(raw === "" ? undefined : Number(raw));
    },
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.error_container}>
          <div className={styles.total_amount_container}>
            <AmountCard
              title={t("lbl_settlement_total_amount")}
              amount={String(totalAmountDue.toFixed(2))}
            />
            <AmountCard
              title={t("lbl_remaining_settlement_amount")}
              amount={String(remainingSettlementAmount.toFixed(2))}
              isError={showSettlementError}
              amountColor={showSettlementError ? "problem" : "g900"}
            />
          </div>
          {showSettlementError && (
            <p className={classNames(styles["problem"], styles["error_text"])}>
              {t("lbl_settlement_error")}
            </p>
          )}

          <WaffyFeesSummary
            feeAmount={waffyFeesAmount}
            remainingShippingFeeAmount={waffyExtraFeesAmount}
          />

          {showWaffyFeesError && (
            <p className={classNames(styles["problem"], styles["error_text"])}>
              {t("lbl_waffy_fees_error", {
                waffyFeesAmount: waffyFeesAmount,
              })}
            </p>
          )}

          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_waffy_fees")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_the_percentage")}
                defaultValue={waffyFeesPercentage}
                maxValue={100}
                type={InputType.NUMBER}
                maxCharacters={5}
                maxDecimals={2}
                handleChange={(e) => {
                  setWaffyFeesPercentage(Number(e.target.value));
                  setShowWaffyFeesError(false);
                }}
                description={t("%")}
                preventMaxValuePass
                placeholder="0.0"
              />
            </div>
          </div>
        </div>
        <div className={styles.sub_section}>
          <h2 className={classNames(styles["subtitle"], styles["h2"])}>
            {t("lbl_the_seller")}
          </h2>
          <div className={styles.total_amount_container}>
            <TextInput
              label={t("lbl_value")}
              maxValue={totalAmountDue - (buyerValue || 0)}
              type={InputType.NUMBER}
              handleChange={(e) => {
                setSellerValue(Number(e.target.value));
                setShowSettlementError(false);
              }}
              description={t("lbl_saudi_currency")}
              value={sellerValue}
              preventMaxValuePass
              placeholder={t("lbl_enter_value")}
            />
            <div className={styles.half_flex}>
              <TextInput
                label={t("lbl_more_details_sheet_fees")}
                type={InputType.PRICE}
                maxValue={sellerValue}
                onPriceChange={(val) => {
                  setSellerFee(val);
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                value={sellerFee}
                preventMaxValuePass
                placeholder={t("lbl_enter_value")}
                isError={showWaffyFeesError && !!sellerValue && sellerFee !== 0}
              />
            </div>
            <TextInput
              label={t("lbl_amount_received")}
              type={InputType.NUMBER}
              description={t("lbl_saudi_currency")}
              disabledValue={
                sellerValue && sellerFee !== undefined
                  ? (sellerValue - sellerFee).toFixed(2)
                  : undefined
              }
              disabled
              placeholder=""
              style={{ color: "#44C16E !important" }}
            />
          </div>
        </div>
        <div className={styles.sub_section}>
          <h2 className={classNames(styles["subtitle"], styles["h2"])}>
            {t("lbl_the_buyer")}
          </h2>
          <div className={styles.total_amount_container}>
            <TextInput
              label={t("lbl_value")}
              maxValue={maxBuyerAmount - (sellerValue || 0)}
              type={InputType.NUMBER}
              handleChange={(e) => {
                setBuyerValue(Number(e.target.value));
                setShowSettlementError(false);
              }}
              description={t("lbl_saudi_currency")}
              value={buyerValue}
              preventMaxValuePass
              placeholder={t("lbl_enter_value")}
            />
            <div className={styles.half_flex}>
              <TextInput
                label={t("lbl_more_details_sheet_fees")}
                type={InputType.PRICE}
                maxValue={buyerValue}
                onPriceChange={(val) => {
                  setBuyerFee(val);
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                value={buyerFee}
                preventMaxValuePass
                placeholder={t("lbl_enter_value")}
                isError={showWaffyFeesError && !!buyerValue && buyerFee !== 0}
              />
            </div>
            <TextInput
              label={t("lbl_amount_received")}
              type={InputType.NUMBER}
              description={t("lbl_saudi_currency")}
              disabledValue={
                buyerValue && buyerFee !== undefined
                  ? (buyerValue - buyerFee).toFixed(2)
                  : undefined
              }
              disabled
              placeholder=""
              style={{ color: "#44C16E !important" }}
            />
          </div>
        </div>

        {contract?.shippable && getShipmentFeesTotal(contract) > 0 && (
          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_shipping_company")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_amount_received")}
                value={naqelFeesDisplay}
                maxValue={shippingFeeAmount}
                type={InputType.NUMBER}
                handleChange={handleNaqelFeesChange}
                description={t("lbl_saudi_currency")}
                preventMaxValuePass
                outerContainerWidth="100%"
                containerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        )}

        <div className={styles.horizontal_line}></div>
        <div className={styles.info_container}>
          <h2 className={styles.info_title}>
            {t("lbl_contract_details_notes")}
          </h2>
          <Textarea
            label={t("lbl_add_description")}
            required={false}
            value={description}
            maxCharacters={500}
            maxInputHeight={"170px"}
            handleChange={(e) => setDescription(e.target.value)}
            minInputHeight="100px"
          />
          <div className={styles["form-scroll"]}>
            {entities?.map((entity) => entity?.file)?.length < 10 && (
              <div key={0} style={{ marginBottom: "0.2rem" }}>
                <ImageUpload withPDF multiple handleChange={handleFileUpload} />
              </div>
            )}
            {entities?.map((entity, index) => (
              <div key={index} className={styles["uploaded_image_container"]}>
                {entity.isLoading && entity.loadingProgress >= 0 ? (
                  <div
                    className={classNames(
                      styles["uploaded_image"],
                      styles["upload_skeleton"]
                    )}>
                    <CircularProgress
                      {...(!entity?.loadingProgress
                        ? {}
                        : {
                            variant: "determinate",
                            value: entity?.loadingProgress,
                          })}
                    />
                  </div>
                ) : (
                  <>
                    {entity?.file?.type === "application/pdf" ? (
                      <PDFComponent
                        fileUrl={entity?.s3File}
                        fileName={entity?.file?.name}
                        isPreview
                      />
                    ) : (
                      <img
                        className={styles["uploaded_image"]}
                        src={entity?.s3File}
                      />
                    )}
                  </>
                )}
                {entity?.s3File && (
                  <div
                    onClick={() => handleDeleteFile(index)}
                    className={styles["delete_icon"]}>
                    <img src="/assets/images/svg/bin.svg" alt="bin" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <UnregisteredPartiesAlert parties={unregisteredParties} />
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: buttonDisabled() ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: buttonDisabled() ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={buttonDisabled()}>
          {propose ? t("lbl_propose_settlement") : t("lbl_confirm_settlement")}
        </PrimaryButton>
        {/* <PrimaryButton
          style={{
            backgroundColor: "#0C4BC41A",
            height: "48px",
            maxWidth: "234px",
            color: "#0C4BC4",
            fontSize: "16px",
            fontWeight: "400",
          }}>
          {t("lbl_save_for_later")}
        </PrimaryButton> */}
      </div>
    </div>
  );
};

export default ManualSettlement;
